/** tm/legal/LegalFilterColumn.tsx — 1열 필터 (정부지원사업 필터 칼럼과 같은 모양 · 폭 176 · 13px): 바로가기(새 소식·★) · 관심 주제 · 구분 · (법령 종류) · 전체 모아보기.
 *  주제 × 구분은 함께 걸린다. 숫자는 한 줄에 하나(누구에게나 같음): 새 글이 있으면 파란 「+n」, 없으면 회색 전체 건수.
 *  빨간 숫자는 「새 소식」 한 곳뿐. 긴 주제 이름은 한 줄로 줄이고 마우스를 올리면 전체 이름(title). 모바일 필터 시트에서도 그대로 쓴다(값은 시트의 임시 선택). */
import type { CSSProperties, ReactNode } from 'react';
import { C, T } from '../components/ui';
import { secTitle, filterItem, num, TABS, MUTED } from './legalTheme';
import type { FeedFacets, FeedTab, LawKind, Topic } from './legalTypes';

export interface FilterValue { topic: string; starred: boolean; onlyNew: boolean; tab: FeedTab; lawKind: LawKind | '' }

interface Props {
  topics: Topic[]; value: FilterValue; alertsTotal: number; loading: boolean; mobile?: boolean;
  /** 지금 조건(검색어·세부 주제 포함)의 구분별 건수 — 없으면 주제 전체 건수 */
  facets?: FeedFacets | null;
  onChange: (next: FilterValue) => void;
}

const redPill: CSSProperties = { background: C.red, color: C.white, borderRadius: 9, padding: '0 6px', fontSize: T.sm, fontWeight: 700, lineHeight: '17px', minWidth: 18, textAlign: 'center' };

function Row({ label, active, n, red, fresh, title, mobile, dim, onClick }: {
  label: ReactNode; active: boolean; n?: number | null; red?: boolean; fresh?: number; title?: string; mobile?: boolean; dim?: boolean; onClick: () => void;
}) {
  // 숫자는 하나: 새 글이 있으면 파란 "+n", 없으면 회색 전체 건수 (정부지원사업처럼 한 줄)
  const hasFresh = !!fresh && fresh > 0;
  const shown = hasFresh ? fresh : n;
  return (
    <button type="button" className={active ? 'lw-f' : 'lw-f lw-fi'} onClick={onClick} aria-current={active ? 'true' : undefined} title={title}
      style={{ ...filterItem(active), fontSize: 13, minHeight: mobile ? 42 : undefined, ...(dim && !active ? { opacity: 0.45 } : null) }}>
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {shown != null && shown > 0 && (
        <span style={{ ...(red ? redPill : { fontSize: T.sm, fontWeight: 600, color: hasFresh ? C.accent : active ? C.accent : MUTED }), fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
          {hasFresh ? `+${num(shown)}` : num(shown)}
        </span>
      )}
    </button>
  );
}

const KINDS: ('' | LawKind)[] = ['', '법률', '시행령', '시행규칙'];

export default function LegalFilterColumn({ topics, value, alertsTotal, loading, mobile, facets, onChange }: Props) {
  const { topic, starred, onlyNew, tab, lawKind } = value;
  const interest = topics.filter(t => !t.feed);
  const flows = topics.filter(t => !!t.feed);
  const current = topics.find(t => t.label === topic) || null;
  const isNew = onlyNew && !starred && topic === '';
  const set = (p: Partial<FilterValue>) => onChange({ ...value, ...p });
  const pickTopic = (label: string) => set({ topic: label, starred: false, onlyNew: false });
  const topicRow = (t: Topic) => {
    const nw = t.alert === false ? t.newCount ?? t.newsNew ?? 0 : t.alerts;
    return <Row key={t.id} mobile={mobile} label={t.label} active={!starred && !isNew && topic === t.label} n={t.counts.all} fresh={nw}
      title={`${t.label} — 전체 ${num(t.counts.all)}건${nw > 0 ? ` · 새 글 ${nw}건` : ''}${t.alert === false ? ' · 알림 끔' : ''}${t.description ? `\n${t.description}` : ''}`}
      onClick={() => pickTopic(t.label)} />;
  };
  const tabCount = (id: FeedTab): number | null => {
    if (facets && facets[id] != null) return facets[id] ?? null;
    return current && !starred && !isNew ? current.counts[id] ?? null : null;
  };
  const sec = (first = false): CSSProperties => ({ ...secTitle, marginTop: first ? 0 : 16 });
  return (
    <nav aria-label="필터">
      <div style={sec(true)}>바로가기</div>
      <Row mobile={mobile} label="🔔 새 소식" active={isNew} n={alertsTotal} red onClick={() => set({ topic: '', starred: false, onlyNew: true, tab: 'all', lawKind: '' })}
        title="확인하지 않은 새 법령 개정·국회 법안·국무회의 의결·입법예고·판례·해석 (뉴스 제외)" />
      <Row mobile={mobile} label="★ 별표" active={starred} onClick={() => set({ topic: '', starred: true, onlyNew: false })} title="상세에서 ☆ 별표한 글" />

      <div style={sec()}>관심 주제</div>
      <Row mobile={mobile} label="전체 주제" active={!starred && !isNew && topic === ''} onClick={() => pickTopic('')} title="모든 주제를 한꺼번에" />
      {loading && !topics.length && [0, 1, 2, 3, 4].map(i => <div key={i} className="lw-skel" aria-hidden="true" style={{ height: 12, margin: '9px 10px', borderRadius: 4, width: `${60 + (i % 3) * 10}%` }} />)}
      {interest.map(topicRow)}

      <div style={sec()}>구분</div>
      {TABS.filter(t => !(isNew && t.id === 'news')).map(t => <Row key={t.id} mobile={mobile} label={t.label} active={tab === t.id} n={tabCount(t.id)}
        dim={t.id !== 'all' && tabCount(t.id) === 0} title={t.id !== 'all' && tabCount(t.id) === 0 ? '이 주제에는 아직 해당 글이 없습니다' : undefined}
        onClick={() => set({ tab: t.id, lawKind: t.id === 'law' ? lawKind : '' })} />)}

      {tab === 'law' && <>
        <div style={sec()}>법령 종류</div>
        {KINDS.map(k => <Row key={k || 'all'} mobile={mobile} label={k || '전체'} active={lawKind === k} onClick={() => set({ lawKind: k })}
          title={k === '시행령' ? '대통령령' : k === '시행규칙' ? '총리령·부령' : undefined} />)}
      </>}

      {flows.length > 0 && <>
        <div style={{ ...sec(), marginBottom: 2 }}>전체 모아보기</div>
        <div style={{ fontSize: T.sm, color: MUTED, lineHeight: 1.5, margin: '0 0 6px 2px' }}>주제와 관계없이 새로 나온 것 전부</div>
        {flows.map(topicRow)}
      </>}
    </nav>
  );
}
